/**
 * Capture bar grammar — parser + payload mapper, ported from the loop worker
 * (apps/web/src/lib/grammar.ts): no /new or /ticket meta-commands, and a
 * small built-in date parser instead of chrono-node.
 *
 * Grammar:
 *   /<cmd> [@<mention>|!<assignee>|#<tag>|due:<date>]* <title text>
 *   [\n<description line…>]
 *
 * `@` mentions contacts, `!` assigns (contact or company), `#` tags a CRM
 * object (company/account, pipeline lead, event, or meeting — Loop's
 * #project repointed at CRM entities), `due:` sets the due date (fire date
 * for /remind). Operators only parse on the title line.
 */

export type Cmd = "action" | "owe" | "bringup" | "decision" | "note" | "remind";

export const CMDS: ReadonlyArray<Cmd> = [
    "action", "owe", "bringup", "decision", "note", "remind",
];

/** Old spellings still accepted when typed; never suggested. */
export const CMD_ALIASES: Record<string, Cmd> = { loop: "owe", ask: "bringup" };

export const CMD_HELP: Record<Cmd, string> = {
    action:   "@who does it — their to-do, I'm waiting on them",
    owe:      "@who I promised — my to-do, owed to them",
    bringup:  "@who to raise it with next time we talk",
    decision: "a decision made with the client",
    note:     "freeform observation",
    remind:   "a future signal to my future self (needs due:)",
};

/** One-line example shown under each command in the slash menu. */
export const CMD_EXAMPLE: Record<Cmd, string> = {
    action:   "/action send the revised deck @dana due:fri #acme",
    owe:      "/owe intro to our CFO @dana due:mon",
    bringup:  "/bringup pricing for the Q4 renewal @dana",
    decision: "/decision we go with the annual plan #acme",
    note:     "/note prefers async updates over calls @dana",
    remind:   "/remind check in on the pilot due:2026-10-01",
};

export type TagKind = "company" | "lead" | "event" | "meeting";

export const TAG_KIND_LABEL: Record<TagKind, string> = {
    company: "Accounts", lead: "Leads", event: "Events", meeting: "Meetings",
};

export interface DirectoryEntry {
    id: string;
    /** Operator label the user types (e.g. `!dana`, `#acme`). */
    slug: string;
    aliases?: string[];
    name: string;
    kind: "contact" | TagKind;
}

export interface Directory {
    contacts: DirectoryEntry[];
    companies: DirectoryEntry[];
    /** `#` pool: companies + leads + events + meetings. */
    targets: DirectoryEntry[];
}

/** Sentinel id for the synthetic "me" entry — assignee=self means null. */
export const SELF_ID = "self";

const matchEntry = (pool: DirectoryEntry[], raw: string) => {
    const key = raw.toLowerCase();
    return (
        pool.find((e) => e.slug.toLowerCase() === key) ??
        pool.find((e) => (e.aliases ?? []).some((a) => a.toLowerCase() === key)) ??
        pool.find((e) => e.id.toLowerCase() === key) ??
        pool.find((e) => e.name.toLowerCase().startsWith(key)) ??
        null
    );
};

export interface ParsedState {
    cmd: Cmd | null;
    assignee: string | null;
    assigneeKind: "contact" | "company" | null;
    mentions: string[];
    /** Resolved `#` tags, order-preserving, deduped by id. */
    links: Array<{ id: string; kind: TagKind }>;
    due: string | null;      // raw token, normalized at payload time
    content: string;
    description: string;
}

export const EMPTY_STATE: ParsedState = {
    cmd: null, assignee: null, assigneeKind: null,
    mentions: [], links: [], due: null, content: "", description: "",
};

const isSpace = (s: string) => /^[ \t]+$/.test(s);

export function parseComposer(text: string, dir: Directory): ParsedState {
    const out: ParsedState = { ...EMPTY_STATE, mentions: [], links: [] };

    const trimmed = text.replace(/^\s*\n+/, "");
    if (!trimmed.startsWith("/")) return out;

    const firstNewline = trimmed.indexOf("\n");
    const titleLine = firstNewline === -1 ? trimmed : trimmed.slice(0, firstNewline);
    const description = firstNewline === -1
        ? ""
        : trimmed.slice(firstNewline + 1).replace(/^\n+/, "").trimEnd();

    const tokens = titleLine.split(/([ \t]+)/).filter((t) => t.length > 0);
    let i = 0;
    while (i < tokens.length && isSpace(tokens[i]!)) i++;

    const cmdToken = tokens[i++];
    if (!cmdToken || !cmdToken.startsWith("/")) return out;
    const raw = cmdToken.slice(1).toLowerCase();
    const cmd = (CMD_ALIASES[raw] ?? raw) as Cmd;
    if (!(CMDS as ReadonlyArray<string>).includes(cmd)) return out;

    out.cmd = cmd;
    out.description = description;

    const assignable = [...dir.contacts, ...dir.companies];
    const contentParts: string[] = [];
    // Leading-@-as-assignee coercion: "/action @dana send the deck" means
    // dana owns it, not "mine, dana also mentioned".
    const cmdHasAssignee = cmd === "action" || cmd === "bringup" || cmd === "owe";
    let firstNonSpaceToken = true;

    for (; i < tokens.length; i++) {
        const tok = tokens[i]!;
        if (isSpace(tok)) {
            if (contentParts.length) contentParts.push(" ");
            continue;
        }
        if (firstNonSpaceToken && cmdHasAssignee && out.assignee === null &&
            tok.startsWith("@") && tok.length > 1) {
            const hit = matchEntry(assignable, tok.slice(1));
            if (hit) {
                out.assignee = hit.id;
                out.assigneeKind = hit.kind === "company" ? "company" : "contact";
                firstNonSpaceToken = false;
                continue;
            }
        }
        firstNonSpaceToken = false;

        if (tok.startsWith("@") && tok.length > 1) {
            const hit = matchEntry(dir.contacts, tok.slice(1));
            if (hit) {
                if (!out.mentions.includes(hit.id)) out.mentions.push(hit.id);
                contentParts.push(tok);
                continue;
            }
        }
        if (tok.startsWith("!") && tok.length > 1) {
            const hit = matchEntry(assignable, tok.slice(1));
            if (hit) {
                out.assignee = hit.id;
                out.assigneeKind = hit.kind === "company" ? "company" : "contact";
                continue;
            }
        }
        if (tok.startsWith("#") && tok.length > 1) {
            const hit = matchEntry(dir.targets, tok.slice(1));
            if (hit && hit.kind !== "contact") {
                if (!out.links.some((l) => l.id === hit.id)) {
                    out.links.push({ id: hit.id, kind: hit.kind });
                }
                continue;
            }
        }
        if (tok.startsWith("due:")) {
            out.due = tok.slice(4) || null;
            continue;
        }
        contentParts.push(tok);
    }

    out.content = contentParts.join("").replace(/[ \t]+/g, " ").trim();
    return out;
}

/** Inverse of parseComposer — canonical composer text for a state. Used by
 *  the structured fields form so edits round-trip back into the line. */
export function serializeState(state: ParsedState, dir: Directory): string {
    if (!state.cmd) return "";
    const slugOf = (id: string, pool: DirectoryEntry[]) => pool.find((e) => e.id === id)?.slug ?? id;
    const parts: string[] = [`/${state.cmd}`];
    if (state.assignee === SELF_ID) parts.push("!me");
    else if (state.assignee) parts.push(`!${slugOf(state.assignee, [...dir.contacts, ...dir.companies])}`);
    for (const l of state.links) parts.push(`#${slugOf(l.id, dir.targets)}`);
    if (state.due) parts.push(`due:${state.due}`);
    if (state.content) parts.push(state.content);
    for (const m of state.mentions) {
        const tok = `@${slugOf(m, dir.contacts)}`;
        if (!state.content.includes(tok)) parts.push(tok);
    }
    let text = parts.join(" ");
    if (state.description) text += `\n${state.description}`;
    return text;
}

// ── Trigger detection — drives the @/! autocomplete popover ────────────────

export interface TriggerHit {
    char: "@" | "!" | "#";
    query: string;
    queryStart: number;
}

export function detectTrigger(text: string, caret: number): TriggerHit | null {
    let i = caret - 1;
    while (i >= 0) {
        const ch = text[i]!;
        if (ch === "@" || ch === "!" || ch === "#") {
            const prev = i > 0 ? text[i - 1]! : " ";
            if (!/\s/.test(prev) && i !== 0) return null;
            const query = text.slice(i + 1, caret);
            if (/\s/.test(query)) return null;
            return { char: ch as TriggerHit["char"], query, queryStart: i };
        }
        if (/\s/.test(ch)) return null;
        i--;
    }
    return null;
}

// ── Date normalization — YYYY-MM-DD, today/tomorrow, weekdays, +N units ────

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Normalize a due: token to YYYY-MM-DD. Accepts ISO dates, today/tomorrow,
 *  weekday names ("friday", "next friday" — both mean the next occurrence),
 *  and relative offsets ("3d", "2w", "1m"). Null when unparseable. */
export function normalizeDate(raw: string | null | undefined, ref = new Date()): string | null {
    if (!raw) return null;
    const t = raw.trim().toLowerCase().replace(/^next-?\s*/, "");
    if (!t) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const base = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
    if (t === "today") return toIso(base);
    if (t === "tomorrow") return toIso(new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1));
    const wd = WEEKDAYS.findIndex((w) => w === t || w.slice(0, 3) === t);
    if (wd >= 0) {
        let delta = (wd - base.getDay() + 7) % 7;
        if (delta === 0) delta = 7; // "friday" on a Friday = next Friday
        return toIso(new Date(base.getFullYear(), base.getMonth(), base.getDate() + delta));
    }
    const rel = /^(\d{1,3})(d|w|m)$/.exec(t);
    if (rel) {
        const n = Number(rel[1]);
        if (rel[2] === "d") return toIso(new Date(base.getFullYear(), base.getMonth(), base.getDate() + n));
        if (rel[2] === "w") return toIso(new Date(base.getFullYear(), base.getMonth(), base.getDate() + n * 7));
        return toIso(new Date(base.getFullYear(), base.getMonth() + n, base.getDate()));
    }
    return null;
}

// ── Payload mapping ────────────────────────────────────────────────────────

import type { CreateRecordBody } from "./api";

/** Build the Tiptap details doc from Shift-Enter description lines; null
 *  keeps the row headline-only (hides the More toggle). */
export function buildDetailsDoc(description: string): string | null {
    const d = description.trim();
    if (!d) return null;
    return JSON.stringify({
        type: "doc",
        content: d.split(/\n+/).map((p) => ({
            type: "paragraph",
            content: p ? [{ type: "text", text: p }] : [],
        })),
    });
}

/** Map a parsed state to the createRecord payload. Null = not submittable
 *  (no cmd/content, or /remind without a parseable date). */
export function stateToPayload(state: ParsedState): CreateRecordBody | null {
    if (!state.cmd || !state.content.trim()) return null;

    const content = state.content.trim();
    const details = buildDetailsDoc(state.description);
    const due = normalizeDate(state.due);
    const assignee = state.assignee === SELF_ID ? null : state.assignee;
    const assigneeKind = state.assignee === SELF_ID ? null : state.assigneeKind;
    const mentions = Array.from(new Set(
        state.mentions.filter((id) => id !== SELF_ID && !(assigneeKind === "contact" && id === assignee)),
    ));

    const attach = (p: CreateRecordBody): CreateRecordBody => {
        if (mentions.length > 0) p.mentionContactIds = mentions;
        if (state.links.length > 0) {
            p.links = state.links.map((l) => ({ targetType: l.kind, targetId: l.id }));
        }
        if (details) p.details = details;
        return p;
    };
    const withAssignee = (p: CreateRecordBody): CreateRecordBody => {
        if (assignee) {
            if (assigneeKind === "company") p.assigneeCompanyId = assignee;
            else p.assigneeContactId = assignee;
        }
        if (due) p.dueDate = due;
        return p;
    };

    switch (state.cmd) {
        case "owe":
            // A promise to a contact: they're the assignee (who it's owed
            // to), the closing is on me — shows in their bring-ups.
            return attach(withAssignee({ type: "loop", content }));
        case "bringup":
            return attach(withAssignee({ type: "ask", content }));
        case "action":
            return attach(withAssignee({ type: "action", content }));
        case "decision":
            return attach({ type: "decision", content });
        case "note":
            return attach({ type: "note", content });
        case "remind":
            if (!due) return null; // reminders require a parseable fire date
            return attach({ type: "reminder", content, fireDate: due });
        default:
            return null;
    }
}

/** Inverse of buildDetailsDoc: flatten a Tiptap doc string to plain text
 *  lines for textarea editing. Non-paragraph nodes contribute their text. */
export function docToPlainText(doc: string | null | undefined): string {
    if (!doc) return "";
    try {
        const parsed = JSON.parse(doc) as { content?: Array<{ content?: Array<{ text?: string }> }> };
        return (parsed.content ?? [])
            .map((block) => (block.content ?? []).map((n) => n.text ?? "").join(""))
            .join("\n")
            .trimEnd();
    } catch {
        return "";
    }
}
